/**
 * Email Analysis Utility Functions
 * Shared utilities for parsing, formatting, and decoding email data
 */

import {
  parseToLocalDate,
  formatDateTime,
  formatDateOnly as formatDateOnlyUtil,
  formatTimeOnly as formatTimeOnlyUtil,
} from "../../../utils/DateTimeUtil";
import DOMPurify from "dompurify";

const ALLOWED_HTML_TAGS = ["p", "br", "strong", "em", "ul", "ol", "li"];

/**
 * Decode HTML entities and sanitize HTML for safe rendering.
 * Handles cases where reasoning text contains encoded HTML entities
 * (e.g., &lt;p&gt;) that need to be decoded before rendering.
 * @param {string} html - The HTML string to decode and sanitize
 * @returns {string} - The decoded and sanitized HTML string
 */
export const decodeAndSanitizeHtml = (html) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html || "";
  return DOMPurify.sanitize(txt.value, {
    ALLOWED_TAGS: ALLOWED_HTML_TAGS,
    ALLOWED_ATTR: [],
  });
};

/**
 * Decode quoted-printable encoded strings and fix common UTF-8 encoding issues
 * @param {string} encodedString - The string to decode
 * @returns {string} - The decoded string
 */
export const decodeQuotedPrintable = (encodedString) => {
  if (!encodedString || typeof encodedString !== "string") {
    return "";
  }

  // Check for quoted-printable encoding patterns
  const hasQuotedPrintableEncoding =
    /=[0-9A-Fa-f]{2}/.test(encodedString) || /=\r?\n/.test(encodedString);

  let decodedString = encodedString;

  // Decode quoted-printable if present
  if (hasQuotedPrintableEncoding) {
    try {
      // Replace =XX hex codes with %XX for URL decoding
      const urlEncodedString = encodedString.replace(
        /=([0-9A-Fa-f]{2})/g,
        "%$1",
      );
      // Remove soft line breaks (= at end of line)
      const noSoftBreaks = urlEncodedString.replace(/=\r?\n/g, "");
      decodedString = decodeURIComponent(noSoftBreaks);
    } catch (error) {
      console.error("Error decoding Quoted-Printable string:", error);
      decodedString = encodedString;
    }
  }

  // Fix common UTF-8 encoding issues that may remain
  decodedString = decodedString
    // Fix UTF-8 BOM and related sequences
    .replace(/^\uFEFF/, "") // Remove BOM
    .replace(/â€™/g, "'") // Right single quotation mark
    .replace(/â€œ/g, '"') // Left double quotation mark
    .replace(/â€/g, '"') // Right double quotation mark
    .replace(/â€"/g, "—") // Em dash
    .replace(/â€"/g, "–") // En dash
    .replace(/â€¦/g, "…") // Horizontal ellipsis
    .replace(/â€¢/g, "•") // Bullet
    .replace(/ââ/g, "") // Remove double â characters
    .replace(/â([a-zA-Z])/g, "$1") // Remove â before letters
    // Fix common accented characters
    .replace(/Ã¡/g, "á")
    .replace(/Ã©/g, "é")
    .replace(/Ã­/g, "í")
    .replace(/Ã³/g, "ó")
    .replace(/Ãº/g, "ú")
    .replace(/Ã±/g, "ñ")
    .replace(/Ã¼/g, "ü")
    .replace(/Ã¶/g, "ö")
    .replace(/Ã¤/g, "ä")
    .replace(/Ã /g, "à")
    .replace(/Ã¨/g, "è")
    .replace(/Ã²/g, "ò")
    .replace(/Ã¹/g, "ù")
    .replace(/Ã§/g, "ç");

  return decodedString;
};

/**
 * @deprecated Use parseToLocalDate from DateTimeUtil.js
 * Parse Zulu time (ISO 8601) strings into Date objects
 * @param {string|Date} dateString - The date string to parse
 * @returns {Date} - The parsed Date object
 */
export const parseZuluTime = (dateString) => {
  return parseToLocalDate(dateString);
};

/**
 * Format a date string to a full locale string with date and time
 * Uses centralized DateTimeUtil for consistent formatting
 * @param {string|Date} dateString - The date to format
 * @returns {string} - The formatted date string
 */
export const formatDate = (dateString) => {
  return formatDateTime(dateString);
};

/**
 * Format a date string to show only the date portion
 * Uses centralized DateTimeUtil for consistent formatting
 * @param {string|Date} dateString - The date to format
 * @returns {string} - The formatted date string (date only)
 */
export const formatDateOnly = (dateString) => {
  return formatDateOnlyUtil(dateString, "short");
};

/**
 * Format a date string to show only the time portion
 * Uses centralized DateTimeUtil for consistent formatting
 * @param {string|Date} dateString - The date to format
 * @returns {string} - The formatted time string
 */
export const formatTimeOnly = (dateString) => {
  return formatTimeOnlyUtil(dateString, true);
};

/**
 * Parse risk factors from the analysis data
 * @param {Array} riskFactors - Array of risk factor strings
 * @returns {Array} - Parsed risk factors
 */
export const parseRiskFactors = (riskFactors) => {
  if (!riskFactors || !Array.isArray(riskFactors)) {
    return [];
  }
  return riskFactors.map((factor) => {
    if (typeof factor === "string") {
      const colonIndex = factor.lastIndexOf(":");
      if (colonIndex > -1) {
        const description = factor.substring(0, colonIndex);
        const score = factor.substring(colonIndex + 1);
        if (!isNaN(score) && score.trim() !== "") {
          return description;
        }
      }
      return factor;
    }
    return factor;
  });
};

/**
 * Extract scam flag from analysis result.
 * Backend returns standardized camelCase field name: isScam
 * @param {Object} analysisResult - The analysis result object
 * @returns {boolean} - true if scam detected, false otherwise
 */
export const getScamFlag = (analysisResult) => {
  if (!analysisResult) {
    return false;
  }

  return analysisResult.isScam || false;
};
